import AdjustmentDialogs from '~/components/shared/AdjustmentDialogs';
import {WarrantyInfoDialog} from '~/components/shared/ActivityDetails';
import trace from '~/lib/trace';
import React, {useEffect, useState} from 'react';
import {View, ActivityIndicator} from 'react-native';
import {Divider} from 'react-native-paper';
import sy from '~/styles';
import {
  Text,
  Pressable,
  Dialog,
  FormNumberInput,
  RobTaskIcon,
  MoreMenu,
  WarrantyIcon,
  ActionButton,
  Grouping,
} from '~/components/controls';
import {Row as GroupingRow} from '~/components/controls/Grouping';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import {LeniencyActionsheet} from '~/components/shared/Actionsheets';
import {DamageNotAllowedDialog} from '~/components/shared/Dialogs';
import {
  service_request as serviceRequestApi,
  rob as robApi,
} from '~/api/private';
import Format from '~/lib/format';
import {isRobLeniencyTask} from '~/types/rob';
import HandshakeIcon from '~/images/md-icons/handshake/materialsymbolsoutlined/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import CheckIcon from '~/images/md-icons/check/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import InfoIcon from '~/images/md-icons/info/materialicons/24px.svg';
import PropTypes from 'prop-types';
export const calculateSlaTaskAmount = ({
  warranty,
  task,
  parts,
  discount = 0,
  markup = 0,
}) => {
  let amount;
  if (warranty?.value === 'Warranty') {
    amount = 0;
  } else if (isRobLeniencyTask(warranty?.value)) {
    amount = warranty?.leniency_amount;
  } else {
    amount =
      task?.amount +
      parts?.reduce((acc, part) => acc + part.price * part.quantity, 0) +
      markup -
      discount;
  }
  return amount;
};
const TaskAmount = ({warranty, task, parts, discount, markup, style}) => {
  const amount = calculateSlaTaskAmount({
    warranty,
    task,
    parts,
    discount,
    markup,
  });
  return <Text style={style}>{Format.price(amount)}</Text>;
};
TaskAmount.propTypes = {
  warranty: PropTypes.any,
  task: PropTypes.any,
  parts: PropTypes.any,
  discount: PropTypes.any,
  markup: PropTypes.any,
  style: PropTypes.any,
};
const SLA = ({
  onDismiss,
  license,
  is_fleet_car,
  is_retail_car,
  request_id,
  damage_id,
  task_id,
  rob,
  component,
  is_damage_request,
  onChange,
  onDelete,
  onShowDenialReasons,
}) => {
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [parts, setParts] = useState(null);
  const [robComponents, setRobComponents] = useState(null);
  const [showEditNumber, setShowEditNumber] = useState(null);
  const [warranty, setWarranty] = useState(null);
  const [taskDiscount, setTaskDiscount] = useState(0);
  const [hasDiscount, setHasDiscount] = useState(false);
  const [taskMarkup, setTaskMarkup] = useState(0);
  const [hasMarkup, setHasMarkup] = useState(false);
  const toggleHasDiscount = () => {
    setHasDiscount((prevState) => {
      const newState = !prevState;
      if (newState) {
        // Reset markup
        setTaskMarkup(0);
      } else {
        setTaskDiscount(0);
      }
      return newState;
    });
  };
  const [componentsWithDamageAllowed, setComponentsWithDamageAllowed] =
    useState(true);
  const toggleHasMarkup = () => {
    setHasMarkup((prevState) => {
      const newState = !prevState;
      if (newState) {
        // Reset discount
        setTaskDiscount(0);
      } else {
        setTaskMarkup(0);
      }
      return newState;
    });
  };
  const [showLeniencyActionsheet, setShowLeniencyActionsheet] = useState(false);
  const [showWarrantyInfoDialog, setShowWarrantyInfoDialog] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(null);
  const [showMarkupDialog, setShowMarkupDialog] = useState(null);
  const [showDamageNotAllowedDialog, setShowDamageNotAllowedDialog] =
    useState(false);
  useEffect(() => {
    const fetchTask = async () => {
      const {result} = await serviceRequestApi.task(
        request_id,
        task_id,
        'task',
      );
      const {task, parts} = result;
      setTask(task);
      setParts(parts);
      setTaskDiscount(task.discount);
      setHasDiscount(task.discount > 0);
      setTaskMarkup(task.markup);
      setHasMarkup(task.markup > 0);
      if (task.options) {
        if (task.options.discount === false) {
          setHasDiscount(false);
        }
        if (task.options.markup === false) {
          setHasMarkup(false);
        }
      }
      const task_amount =
        task?.amount +
        parts?.reduce((acc, part) => acc + part.price * part.quantity, 0);
      setWarranty({
        value: task.warranty,
        leniency_amount: task.leniency_amount ?? task_amount,
        is_damage: task.is_damage,
      });
    };
    const fetchRobComponents = async () => {
      const {result: robComponentsData} = is_damage_request
        ? await robApi.damage_components(license, request_id)
        : await robApi.components(license, request_id);
      setRobComponents(robComponentsData);
    };
    const fetchRobContract = async () => {
      const {result: rob_contract} = await robApi.contract(license, request_id);
      setComponentsWithDamageAllowed(
        rob_contract?.supplierContract.repairAndMaintenance
          .componentsWithDamageAllowed,
      );
    };
    const fetch = async () => {
      // The calls are independent, so run them side by side instead of
      // waiting for each one before starting the next.
      await Promise.all([
        fetchTask(),
        ...(!is_fleet_car && !is_retail_car
          ? [fetchRobComponents(), fetchRobContract()]
          : []),
      ]);
      setLoading(false);
    };
    fetch().catch(trace);
  }, [request_id, license, task_id]);
  const onOK = () => {
    onChange({
      parts,
      warranty: warranty.value,
      leniency_amount: isRobLeniencyTask(warranty.value)
        ? warranty.leniency_amount
        : null,
      is_damage: warranty.is_damage,
      discount: taskDiscount,
      markup: taskMarkup,
    });
  };
  const onPartQuantityChange = (partIndex, part, value) => {
    setParts((prevParts) => {
      const nextParts = [...prevParts];
      nextParts[partIndex] = {
        ...part,
        quantity: value,
      };
      return nextParts;
    });
  };
  const openPartQuantityEditor = (part, partIndex) => {
    setShowEditNumber({
      title: part.description,
      value: part.quantity,
      onChange: (value) => onPartQuantityChange(partIndex, part, value),
    });
  };
  let amount;
  if (warranty?.value === 'Warranty') {
    amount = 0;
  } else if (isRobLeniencyTask(warranty?.value)) {
    amount = warranty?.leniency_amount;
  } else {
    amount = task?.amount;
  }
  if (showLeniencyActionsheet) {
    const task_amount =
      task?.amount +
      parts?.reduce((acc, part) => acc + part.price * part.quantity, 0);
    return (
      <LeniencyActionsheet
        onDismiss={() => setShowLeniencyActionsheet(false)}
        value={warranty.value}
        task_amount={task?.leniency_amount ?? task_amount}
        onChange={(leniency, leniency_amount) => {
          setWarranty({
            ...warranty,
            value: leniency,
            leniency_amount,
            is_damage: false,
          });
          setShowLeniencyActionsheet(false);
        }}
      />
    );
  }
  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        {loading && (
          <View style={[sy['p-4']]}>
            <ActivityIndicator size="large" color="#231fda" />
          </View>
        )}
        {!loading && (
          <>
            <Title style={[sy['mx-4']]}>
              <View
                style={[
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['items-center'],
                  sy['flex-1'],
                  {
                    width: '100%',
                  },
                ]}>
                <View
                  style={[sy['flex-1'], sy['flex-row'], sy['items-center']]}>
                  <Text
                    style={[
                      sy.truncate,
                      sy.largePlus,
                      {
                        lineHeight: 30,
                      },
                    ]}>
                    {robComponents?.[task.rob_code]?.description ??
                      task.description}
                  </Text>
                </View>
                {onDelete && (
                  <Pressable onPress={() => onDelete(task.id)}>
                    <DeleteIcon fill="#4a4a49" />
                  </Pressable>
                )}
                {rob?.status.code && (
                  <Pressable
                    style={[sy['flex-row']]}
                    disabled={is_retail_car || is_fleet_car}
                    onPress={() => onShowDenialReasons()}>
                    <RobTaskIcon
                      statuses={[rob?.status.code]}
                      height="24"
                      width="24"
                    />
                  </Pressable>
                )}
              </View>
            </Title>
            <Content
              style={{
                paddingBottom: 0,
                paddingHorizontal: 0,
              }}>
              <Grouping
                header="Arbeid"
                initial={{
                  expand: true,
                }}>
                <View style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}>
                  <View style={[sy['shrink-0']]}>
                    <HandshakeIcon />
                  </View>
                  <View style={[sy['flex-1']]}>
                    <View
                      style={[
                        sy['flex-row'],
                        sy['justify-between'],
                        sy['gap-4'],
                      ]}>
                      <Text>{task.description}</Text>
                      <View
                        style={[
                          sy['flex-row'],
                          sy['justify-end'],
                          sy['gap-2'],
                        ]}>
                        {taskDiscount > 0 && (
                          <DiscountIcon fill="#299976" width={18} height={18} />
                        )}
                        {taskMarkup > 0 && (
                          <SellIcon fill="#FF6C00" width={18} height={18} />
                        )}
                        <Pressable
                          disabled={!hasDiscount && !hasMarkup}
                          onPress={() => {
                            if (hasDiscount) {
                              setShowDiscountDialog({
                                discount: taskDiscount,
                                amount,
                                onChange: setTaskDiscount,
                              });
                            }
                            if (hasMarkup) {
                              setShowMarkupDialog({
                                markup: taskMarkup,
                                amount,
                                onChange: setTaskMarkup,
                              });
                            }
                          }}>
                          <Text
                            style={[
                              sy.smallRegular,
                              (hasDiscount || hasMarkup) && sy.underline,
                            ]}>
                            {Format.price(amount + taskMarkup - taskDiscount)}
                          </Text>
                        </Pressable>
                      </View>
                    </View>

                    <Text style={[sy.small, sy['text-lightgray']]}>SLA</Text>
                  </View>
                </View>
              </Grouping>
              {parts?.length > 0 && (
                <>
                  <Divider style={sy['bg-gray88']} />
                  <View style={[sy['p-4']]}>
                    <Text style={[sy.mediumBold]}>Artikel(en)</Text>
                  </View>
                  {parts?.map((part, index) => (
                    <React.Fragment key={part.id ?? part.description}>
                      <Divider style={sy['bg-gray88']} />
                      <View style={[sy['p-4'], sy['flex-row'], sy['gap-4']]}>
                        <View>
                          <Pressable
                            style={[
                              {
                                minWidth: 24,
                                minHeight: 24,
                                borderRadius: 2,
                              },
                              sy['bg-gray95'],
                              sy['p-1'],
                              sy['items-center'],
                            ]}
                            onPress={() => openPartQuantityEditor(part, index)}>
                            <Text style={sy.xSmallMedium}>
                              {Format.number(part.quantity, 2, true, {
                                minimumFractionDigits: 0,
                              })}
                            </Text>
                          </Pressable>
                        </View>
                        <View style={[sy['flex-1'], sy['flex-row']]}>
                          <View style={[sy['flex-1']]}>
                            <Text>{part.description}</Text>
                            <Text style={[sy.small, sy['text-lightgray']]}>
                              SLA&nbsp;&middot;&nbsp;{task.lessor}
                            </Text>
                          </View>
                          <View style={[sy['items-end']]}>
                            <Text style={[sy.smallRegular]}>
                              {Format.price(part.price * part.quantity)}
                            </Text>
                          </View>
                        </View>
                      </View>
                    </React.Fragment>
                  ))}
                </>
              )}
              {!is_fleet_car && !is_retail_car && (
                <Grouping
                  header="ROB-Net"
                  initial={{
                    expand: true,
                  }}>
                  <GroupingRow
                    title={<Text style={sy.mediumPlus}>Activiteit</Text>}>
                    <Text>
                      {task.rob_code}&nbsp;&middot;&nbsp;
                      {robComponents[task.rob_code]?.description}
                    </Text>
                  </GroupingRow>
                </Grouping>
              )}
            </Content>
            <Divider style={sy['bg-gray88']} />
            <Actions style={[sy['justify-between']]}>
              <View style={[sy['flex-row'], sy['items-center']]}>
                <TaskAmount
                  warranty={warranty}
                  task={task}
                  parts={parts}
                  discount={taskDiscount}
                  markup={taskMarkup}
                  style={[sy.mediumBold, sy['pl-2'], sy['pr-4']]}
                />
                {is_damage_request ? (
                  <MoreMenu
                    icon={
                      <View
                        style={[
                          sy['flex-row'],
                          sy['items-center'],
                          sy['gap-2'],
                        ]}>
                        {hasDiscount && <DiscountIcon fill="#299976" />}
                        {hasMarkup && <SellIcon fill="#FF6C00" />}
                        <WarrantyIcon
                          value={{
                            is_damage: true,
                          }}
                        />
                      </View>
                    }
                    items={[
                      {
                        title: 'Korting',
                        disabled: task?.options?.discount === false,
                        onPress: () => {
                          if (hasMarkup) {
                            toggleHasMarkup();
                          }
                          toggleHasDiscount();
                        },
                        leadingIcon: hasDiscount ? CheckIcon : null,
                      },
                      {
                        title: 'Toeslag',
                        disabled: task?.options?.markup === false,
                        onPress: () => {
                          if (hasDiscount) {
                            toggleHasDiscount();
                          }
                          toggleHasMarkup();
                        },
                        leadingIcon: hasMarkup ? CheckIcon : null,
                      },
                    ]}
                  />
                ) : (
                  <MoreMenu
                    icon={
                      <View style={[sy['flex-row'], sy['items-center']]}>
                        {hasDiscount && <DiscountIcon fill="#299976" />}
                        {hasMarkup && <SellIcon fill="#FF6C00" />}
                        {((!hasDiscount && !hasMarkup) ||
                          warranty?.value ||
                          warranty?.is_damage) && (
                          <WarrantyIcon
                            value={{
                              warranty: warranty?.value,
                              is_damage: warranty?.is_damage,
                            }}
                          />
                        )}
                      </View>
                    }
                    items={[
                      {
                        title: 'Korting',
                        disabled: task?.options?.discount === false,
                        onPress: () => {
                          if (hasMarkup) {
                            toggleHasMarkup();
                          }
                          toggleHasDiscount();
                          setWarranty({
                            value: null,
                            leniency_amount: null,
                            is_damage: false,
                          });
                        },
                        leadingIcon: hasDiscount ? CheckIcon : null,
                      },
                      {
                        title: 'Toeslag',
                        disabled: task?.options?.markup === false,
                        onPress: () => {
                          if (hasDiscount) {
                            toggleHasDiscount();
                          }
                          toggleHasMarkup();
                          setWarranty({
                            value: null,
                            leniency_amount: null,
                            is_damage: false,
                          });
                        },
                        leadingIcon: hasMarkup ? CheckIcon : null,
                      },
                      {
                        title: 'Garantie',
                        disabled: task?.options?.warranty === false,
                        onPress: () => {
                          if (hasDiscount) {
                            toggleHasDiscount();
                          }
                          if (hasMarkup) {
                            toggleHasMarkup();
                          }
                          if (warranty.value === 'Warranty') {
                            setWarranty({
                              value: null,
                              leniency_amount: null,
                              is_damage: false,
                            });
                          } else {
                            setWarranty({
                              value: 'Warranty',
                              leniency_amount: null,
                              is_damage: false,
                            });
                            setShowWarrantyInfoDialog(true);
                          }
                        },
                        leadingIcon:
                          warranty.value === 'Warranty' ? CheckIcon : null,
                      },
                      {
                        title: 'Coulance',
                        disabled: task?.options?.leniency === false,
                        leadingIcon: isRobLeniencyTask(warranty.value)
                          ? CheckIcon
                          : null,
                        onPress: () => {
                          const isLeniencyTask = isRobLeniencyTask(
                            warranty.value,
                          );
                          if (hasDiscount) {
                            toggleHasDiscount();
                          }
                          if (hasMarkup) {
                            toggleHasMarkup();
                          }
                          if (isLeniencyTask) {
                            setWarranty({
                              value: null,
                              leniency_amount: null,
                              is_damage: false,
                            });
                          } else {
                            setTimeout(
                              () => setShowLeniencyActionsheet(true),
                              100,
                            );
                          }
                        },
                      },
                      {
                        title: (
                          <>
                            {componentsWithDamageAllowed ? (
                              'Schade'
                            ) : (
                              <View style={[sy['flex-row'], sy['gap-4']]}>
                                <Text>Schade</Text>
                                <InfoIcon
                                  fill="#4A4A49"
                                  height={24}
                                  width={24}
                                />
                              </View>
                            )}
                          </>
                        ),
                        disabled: task?.options?.damage === false,
                        onPress: () => {
                          if (componentsWithDamageAllowed) {
                            if (hasDiscount) {
                              toggleHasDiscount();
                            }
                            if (hasMarkup) {
                              toggleHasMarkup();
                            }
                            setWarranty((prevState) => ({
                              value: null,
                              leniency_amount: null,
                              is_damage: !prevState.is_damage,
                            }));
                          } else {
                            setShowDamageNotAllowedDialog(true);
                          }
                        },
                        leadingIcon: warranty.is_damage ? CheckIcon : null,
                      },
                    ]}
                  />
                )}
              </View>
              <View style={[sy['flex-row'], sy['gap-2']]}>
                <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
                <ActionButton onPress={onOK}>OK</ActionButton>
              </View>
            </Actions>
          </>
        )}
      </Actionsheet>
      {showEditNumber && (
        <Dialog
          visible={true}
          title={showEditNumber.title}
          initialValue={showEditNumber.value}
          onDismiss={({initialValue}) => {
            showEditNumber.onChange(initialValue);
            setShowEditNumber(null);
          }}
          buttons={[
            {
              text: 'Annuleren',
              onPress: ({initialValue}) => {
                showEditNumber.onChange(initialValue);
                setShowEditNumber(null);
              },
            },
            {
              text: 'OK',
              onPress: () => setShowEditNumber(null),
            },
          ]}>
          <View style={[sy['py-4']]}>
            <FormNumberInput
              label="Aantal"
              value={showEditNumber.value}
              digits={2}
              onChangeNumber={showEditNumber.onChange}
            />
          </View>
        </Dialog>
      )}
      {showWarrantyInfoDialog && (
        <WarrantyInfoDialog
          title={task.description}
          onDismiss={() => setShowWarrantyInfoDialog(false)}
        />
      )}
      <AdjustmentDialogs
        showDiscountDialog={showDiscountDialog}
        setShowDiscountDialog={setShowDiscountDialog}
        showMarkupDialog={showMarkupDialog}
        setShowMarkupDialog={setShowMarkupDialog}
        includeTaskId={false}
      />
      {showDamageNotAllowedDialog && (
        <DamageNotAllowedDialog
          license={license}
          rob={rob}
          request_id={request_id}
          damage_id={damage_id}
          component={component}
          onDismiss={({close_action_sheet}) => {
            setShowDamageNotAllowedDialog(false);
            if (close_action_sheet) {
              onDismiss();
            }
          }}
        />
      )}
    </>
  );
};
SLA.propTypes = {
  onDismiss: PropTypes.any,
  license: PropTypes.any,
  is_fleet_car: PropTypes.any,
  is_retail_car: PropTypes.any,
  request_id: PropTypes.any,
  damage_id: PropTypes.any,
  task_id: PropTypes.any,
  rob: PropTypes.any,
  component: PropTypes.any,
  is_damage_request: PropTypes.any,
  onChange: PropTypes.any,
  onDelete: PropTypes.any,
  onShowDenialReasons: PropTypes.any,
};
export default SLA;
